import { useEffect, useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { API_ROUTES, LIMITS } from "@ziolo/shared";
import { Button } from "@ziolo/ui";
import { Field } from "./Field.js";
import { apiRequest } from "../lib/apiClient.js";
import { useFormSubmit } from "../hooks/useFormSubmit.js";
import { useSession } from "../session/SessionProvider.js";

export function BugReportDialog({
  page,
  onClose,
}: {
  page: string;
  onClose: () => void;
}) {
  const { user } = useSession();
  const { busy, fields, submit } = useFormSubmit();
  const [body, setBody] = useState("");

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.code === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const ok = await submit(async () => {
      await apiRequest<{ ok: true }>(API_ROUTES.bugReports.create, {
        method: "POST",
        body: { body, page },
      });
    }, "Dzięki. Przeczytamy.");
    if (ok) onClose();
  }

  const remaining = LIMITS.bugReport.max - body.length;

  return createPortal(
    <div className="bug-overlay" role="dialog" aria-label="Zgłoś buga" onClick={onClose}>
      <div className="bug-card" onClick={(event) => event.stopPropagation()}>
        <p className="bug-card__kicker">Zgłoś buga</p>
        <h2 className="bug-card__title">Co nie gra?</h2>
        <p className="bug-card__copy">
          Krótko: co kliknąłeś i co poszło nie tak. Strona {page || "/"} dopisze się sama.
          {user
            ? ` Wyśle się z konta ${user.username}.`
            : " Możesz zgłosić bez logowania."}
        </p>
        <form className="hub-form" onSubmit={(event) => void onSubmit(event)}>
          <Field
            error={fields.body}
            hint={`Pozostało ${remaining} z ${LIMITS.bugReport.max} znaków`}
          >
            <textarea
              className="z-textarea"
              name="body"
              placeholder="Np. po Wypłacie Bazarek nie otwiera się na telefonie…"
              minLength={LIMITS.bugReport.min}
              maxLength={LIMITS.bugReport.max}
              rows={6}
              value={body}
              onChange={(event) => setBody(event.target.value)}
              required
              autoFocus
            />
          </Field>
          <div className="hub-form__actions">
            <Button type="button" variant="ghost" onClick={onClose}>
              Anuluj
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={busy || body.trim().length < LIMITS.bugReport.min}
            >
              {busy ? "Wysyłanie..." : "Wyślij"}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
